import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom Hook für automatische Token-Verlängerung im Kundenportal.
 * 
 * Features:
 * - Automatischer Token-Refresh alle 20 Minuten bei Aktivität
 * - Token-Refresh bei App-Focus (wenn Tab wieder aktiv wird)
 * - Automatischer Logout bei Token-Ablauf
 * - Aktivitätstracking (Maus, Tastatur, Touch)
 */
const useCustomerAuth = () => {
  const navigate = useNavigate();
  const lastActivityRef = useRef(Date.now());
  const refreshIntervalRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Token aus localStorage holen
  const getToken = useCallback(() => {
    return localStorage.getItem('customer_token');
  }, []);

  // Prüfen ob Token bald abläuft (innerhalb von 5 Tagen)
  const isTokenExpiringSoon = useCallback(() => {
    const token = getToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      const fiveDaysFromNow = Date.now() + (5 * 24 * 60 * 60 * 1000);
      
      return expiry < fiveDaysFromNow;
    } catch {
      return true;
    }
  }, [getToken]);

  // Token erneuern
  const refreshToken = useCallback(async () => {
    const token = getToken();
    if (!token || isRefreshingRef.current) return false;

    isRefreshingRef.current = true;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/kunden/refresh-token`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.token) {
          localStorage.setItem('customer_token', data.token);
          console.log('🔄 Token erfolgreich verlängert');
          return true;
        }
      } else if (response.status === 401) {
        // Token ist ungültig - Logout
        console.log('⚠️ Token abgelaufen - Logout');
        handleLogout();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    } finally {
      isRefreshingRef.current = false;
    }

    return false;
  }, [getToken]);

  // Logout Handler
  const handleLogout = useCallback(() => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_id');
    localStorage.removeItem('customer_email');
    localStorage.removeItem('customer_name');
    navigate('/kunden/login');
  }, [navigate]);

  // Aktivität tracken
  const trackActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Periodischer Token-Check
  const checkAndRefresh = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    // Nur refreshen wenn in den letzten 30 Minuten Aktivität war
    const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
    const isActive = lastActivityRef.current > thirtyMinutesAgo;

    if (isActive && isTokenExpiringSoon()) {
      await refreshToken();
    }
  }, [getToken, isTokenExpiringSoon, refreshToken]);

  // Setup Effect
  useEffect(() => {
    const token = getToken();
    if (!token) return;

    // Aktivitäts-Event-Listener
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, trackActivity, { passive: true });
    });

    // Tab-Focus Event - Token refreshen wenn Tab wieder aktiv wird
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        trackActivity();
        // Bei Rückkehr zum Tab sofort prüfen
        checkAndRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodischer Check alle 20 Minuten
    refreshIntervalRef.current = setInterval(checkAndRefresh, 20 * 60 * 1000);

    // Initialer Check nach 5 Sekunden
    const initialCheck = setTimeout(checkAndRefresh, 5000);

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, trackActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      clearTimeout(initialCheck);
    };
  }, [getToken, trackActivity, checkAndRefresh]);

  return {
    token: getToken(),
    refreshToken,
    handleLogout,
    isAuthenticated: !!getToken()
  };
};

export default useCustomerAuth;
